<script setup lang="ts">
import { ref } from 'vue'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import { useGameStore } from '@/stores/gameStore'
import Backend from '@/models/backend'
import MediumModel from '@/components/Interface/Modal/ModelSizes/MediumModel.vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import DeleteButton from '@/components/Elements/Buttons/DeleteButton.vue'
import { logger } from '@/models/logger'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// Store
const gameStore = useGameStore()

// Refs
const cropperRef = ref(null)
const imageUrl = ref('')
const imageLoaded = ref(false)
const isUploading = ref(false)
const imageDimensions = ref({ width: 0, height: 0 })
const fileError = ref('')

// Methods
const openFileBrowser = () => {
  fileError.value = ''

  // Create a temporary file input element
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = 'image/jpeg,image/png'

  // Set up the change event handler
  input.onchange = () => {
    if (input.files && input.files.length > 0) {
      processFile(input.files[0])
    }
  }

  // Trigger the click
  input.click()
}

const processFile = (file: File) => {
  // Check file type
  if (!['image/jpeg', 'image/png'].includes(file.type)) {
    fileError.value = t('modals.editAllianceImage.errors.invalidFormat')
    return
  }

  const reader = new FileReader()
  reader.onload = async (e) => {
    const dataUrl = e.target?.result as string

    try {
      // Check image dimensions
      const dimensions = await checkImageDimensions(dataUrl)
      imageDimensions.value = dimensions

      // Reject if image is too small
      if (dimensions.width < 500 || dimensions.height < 500) {
        fileError.value = t('modals.editAllianceImage.errors.tooSmall')
        return
      }

      // Load the image into the cropper
      imageUrl.value = dataUrl
      imageLoaded.value = true
    } catch (error) {
      fileError.value = t('modals.editAllianceImage.errors.loadFailed')
      logger.error(error)
    }
  }
  reader.readAsDataURL(file)
}

const checkImageDimensions = (dataUrl: string): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      resolve({
        width: img.width,
        height: img.height
      })
    }
    img.onerror = () => {
      reject(new Error('Failed to load image'))
    }
    img.src = dataUrl
  })
}

const onCropperReady = () => {
  // Cropper is ready
}

const resetImage = () => {
  imageUrl.value = ''
  imageLoaded.value = false
  imageDimensions.value = { width: 0, height: 0 }
  fileError.value = ''
}

const uploadImage = async () => {
  if (!cropperRef.value) return

  try {
    isUploading.value = true

    // Get cropped canvas
    const { canvas } = (cropperRef.value as any).getResult()

    // Create a new canvas with exactly 500x500 dimensions
    const finalCanvas = document.createElement('canvas')
    finalCanvas.width = 500
    finalCanvas.height = 500
    const ctx = finalCanvas.getContext('2d')

    if (!ctx) {
      throw new Error('Failed to create canvas context')
    }

    // Draw the cropped image onto the 500x500 canvas
    ctx.drawImage(canvas, 0, 0, 500, 500)

    // Convert canvas to Blob
    const blob = await new Promise<Blob>((resolve, reject) => {
      finalCanvas.toBlob((blob: Blob | null) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('Failed to create blob from canvas'))
        }
      }, 'image/png')
    })

    // Create FormData and append the image
    const formData = new FormData()
    formData.append('image', blob, 'alliance-image.png')

    // Upload the image to the backend
    const backend = new Backend()

    backend
      .updateAllianceImage(formData)
      .then((response) => {
        logger.debug(response)
        gameStore.setTinySuccessToastMessage(t('modals.editAllianceImage.messages.success'))
      })
      .catch((error) => {
        logger.error('Error uploading image:', error)
        gameStore.setTinyErrorToastMessage(t('modals.editAllianceImage.messages.failed'))
      })

    // Close the modal
    gameStore.modalType = ''
  } catch (error) {
    logger.error('Error uploading image:', error)
    gameStore.setTinyErrorToastMessage(t('modals.editAllianceImage.messages.failed'))
  } finally {
    isUploading.value = false
  }
}
</script>

<template>
  <MediumModel :title="t('modals.editAllianceImage.title')">
    <div class="flex flex-col gap-4 items-center p-4">
      <!-- Image Selection Area -->
      <div v-if="!imageLoaded" class="flex flex-col items-center justify-center w-full gap-4">
        <div class="text-center text-slate-400">
          <p class="">{{ t('modals.editAllianceImage.selectImage') }}</p>
          <p class="text-xs text-gray-400">{{ t('modals.editAllianceImage.imageRequirements') }}</p>
        </div>

        <!-- Simple Button Approach -->
        <ActionButton @click="openFileBrowser">
          {{ t('modals.editAllianceImage.selectButton') }}
        </ActionButton>

        <div v-if="fileError" class="text-red-500 text-sm mt-1">
          {{ fileError }}
        </div>
      </div>

      <!-- Cropper Area (when image is loaded) -->
      <div v-if="imageLoaded" class="w-full">
        <div class="cropper-container">
          <Cropper
            class="cropper"
            :src="imageUrl"
            :stencil-props="{
              aspect: 1,
              minAspectRatio: 1,
              maxAspectRatio: 1
            }"
            :min-width="500"
            :min-height="500"
            :auto-zoom="false"
            image-restriction="stencil"
            @ready="onCropperReady"
            ref="cropperRef"
          />
        </div>
        <div class="mt-4 text-center text-sm text-gray-500">
          {{ t('modals.editAllianceImage.cropperHelp') }}
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex gap-3 justify-end w-full mt-2">
        <ActionButton
          v-if="imageLoaded"
          @click="resetImage"
        >
          {{ t('modals.editAllianceImage.changeImage') }}
        </ActionButton>
        <DeleteButton @click="gameStore.modalType = ''">
          {{ t('modals.editAllianceImage.cancel') }}
        </DeleteButton>
        <ActionButton
          v-if="imageLoaded"
          @click="uploadImage"
          :disabled="isUploading"
          :class="{ 'opacity-50 cursor-not-allowed': isUploading }"
        >
          {{ isUploading ? t('modals.editAllianceImage.uploading') : t('modals.editAllianceImage.upload') }}
        </ActionButton>
      </div>
    </div>
  </MediumModel>
</template>

<style scoped>
.cropper-container {
  height: 400px;
  background-color: #f3f4f6;
  border-radius: 4px;
}

.cropper {
  height: 100%;
  width: 100%;
}
</style>
